// deskAnswerState.js — which answer a Desk label, spinner or empty line is allowed to describe (2026-10-23).
//
// Search/filter audit 2026-10-23 (Desk §4–§6 and the map section): several surfaces described an answer
// other than the one that had landed. The typed preview counted the previous search; the green
// "whole base · N" showed the old total before the new one arrived; an old shelf request switched off
// the newer one's spinner and lock; a cancelled street lookup settled as "no streets"; a failed pin
// request left the map drawing loaded rows only, without saying so; "no locations" ignored the
// background dots. The rules live here, pure, so node:test can pin them (deskAnswerState.test.mjs).

/**
 * The typed-search preview row under the box.
 * The feed follows the LANDED text (commitSearch / a restore write `landedSearch`, and keep it equal to
 * the box), not keystrokes. So while the box differs from the landed text, no number on screen answers
 * what is typed: the row shows the understood words and the Enter action, and no count. Before, it
 * showed the previous answer's loaded count beside the new words, and the Enter pill compared residual
 * text only, so a box of fully understood words (empty residual) lost its visible Enter.
 * The green "whole base · N" needs the committed answer itself: landed and no request in flight.
 *
 * Returns { draftPending, count: 'loaded' | 'whole_base_only' | null, action: 'enter' | 'committed' | 'committed_pending' | null }.
 */
export function searchPreviewState({ boxText = '', landedText = '', committedQ = '', serverOnly = false, totalLanded = false, fetching = false } = {}) {
  const draftPending = String(boxText || '').trim() !== String(landedText || '').trim()
  if (draftPending) return { draftPending, count: null, action: 'enter' }
  const answered = !!totalLanded && !fetching
  return {
    draftPending,
    count: !answered ? null : serverOnly ? 'whole_base_only' : 'loaded',
    action: committedQ ? (answered ? 'committed' : 'committed_pending') : null,
  }
}

/**
 * A request lock owned by ONE run. `ref.current` is the owning run's token (truthy) or false.
 * Before, every run cleared a shared boolean in its `finally`, so an older shelf request still awaiting
 * an uncancellable cache read cleared the NEWER run's lock and spinner when it finally returned.
 * releaseRun is true only for the owner, which then also clears its own loading flag.
 */
export function ownRun(ref) { const token = {}; ref.current = token; return token }
export function releaseRun(ref, token) {
  if (ref.current !== token) return false
  ref.current = false
  return true
}

/**
 * What the possible-matches shelf shows. Before, it rendered nothing until the first cards arrived,
 * so a first page in flight, or a first page that failed, left a counted unknown tier with no surface.
 * serverTotal 0 = the server said there is nothing to shelve.
 */
export function shelfSurface({ groups = 0, loading = false, failed = false, serverTotal = null } = {}) {
  if (groups > 0) return 'cards'
  if (serverTotal === 0) return null
  if (failed) return 'failed'
  if (loading) return 'loading'
  return null
}

/**
 * The street facet's catalogue load, latest lookup only. setCatalog receives:
 *   []    no district picked (the facet's resting state), or a district with no recorded streets;
 *   null  this lookup is in flight;
 *   [..]  the list;
 *   false the lookup failed: the facet falls back to the search's own street counts and does not
 *         claim the district is empty.
 * A cancelled lookup (AbortError) or any lookup superseded by a newer key sets nothing. Before, the
 * reader turned an abort into [], and cleanup of the old lookup settled "no streets" over the new one.
 * Returns the effect cleanup.
 */
export function loadStreetCatalog(key, fetchCatalog, setCatalog, warn = () => {}) {
  if (!key) { setCatalog([]); return () => {} }
  setCatalog(null)
  const ac = new AbortController()
  let dead = false
  Promise.resolve()
    .then(() => fetchCatalog(key.split(','), ac.signal))
    .then(list => { if (!dead) setCatalog(Array.isArray(list) ? list : false) },
      e => {
        if (dead || e?.name === 'AbortError') return
        warn(e)
        setCatalog(false)
      })
  return () => { dead = true; ac.abort() }
}

/**
 * The line under the map. Returns 'pins_failed' | 'none' | null.
 *   pins_failed — the full matching pin set could not be fetched, so the map shows the loaded rows only;
 *                 said in words, because those pins look exactly like a complete answer.
 *   none        — nothing at all is on the map: no loaded pin and no background dot.
 * Before, "no locations" read only the loaded pins, and showed while background dots were visible.
 */
export function mapCoverageNote({ loadedPins = 0, backgroundPins = 0, pinsFailed = false } = {}) {
  if (pinsFailed) return 'pins_failed'
  if (loadedPins === 0 && backgroundPins === 0) return 'none'
  return null
}
